import _                                                     from 'underscore';
import classNames                                            from 'classnames';
import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Button, Form, Icon, List, Menu, Modal, Tab }        from 'semantic-ui-react';
import { Formik, FormikConfig, FormikProps }                 from 'formik';
import * as Yup                                              from 'yup';
import { Cell, Grid }                                        from 'styled-css-grid';
import { useMutation, useQuery }                             from '@apollo/react-hooks';

import { Align, CorvalProjectActivity, CorvalRecurrenceType, SemanticUIColor, Size, SystemEvent, UseStateHookFunction, Uuid } from '../../../../types';

import { ProjectActivityEntityChangeCallback, useAuditRecords, useCustomFormikHelpers, useFeatureFlags, UseFeatureFlagsHook, useProjectActivities, useSystemModals, useSystemToasts, useYupExtensions } from '../../../../hooks';
import { DecisionEngineQueries, SystemQueries }                                                                                                                                                         from '../../../../queries';
import { combineValuesByDelimiter, convertEmptyStringToNull, convertNullToEmptyString, runIfFunction }                                                                                                  from '../../../../util';
import { queriesHaveData, queriesLoading, withoutType }                                                                                                                                                 from '../../../../store/_apolloUtil';
import { ENTITY_ICONS, ProjectMilestoneType }                                                                                                                                                           from '../../../../constants/ProductEnums';
import { CenteredContentContainer, StyledContainer }                                                                                                                                                    from '../../../containers';
import { DialogIntroductionContainer }                                                                                                                                                                  from '../../../containers/DialogIntroductionContainer';
import { QueryDialogProps, QueryOpDialog }                                                                                                                                                              from '../../../components/from-query/QueryOpDialog';
import { BasicDialogContainer }                                                                                                                                                                         from '../../../components/modals/BasicDialogContainer';
import { FormTabWithNotifier }                                                                                                                                                                          from '../../../components/display/ItemWithNotifier';
import { AuditMemo }                                                                                                                                                                                    from '../../../components/audits/AuditMemo';
import { ProjectActivityUpdateAreaCost, ProjectActivityUpdateAreaCostDisplayOptions }                                                                                                                   from './ProjectActivityUpdateAreaCost';
import { ProjectActivityUpdateAreaDetails, ProjectActivityUpdateAreaDetailsDisplayOptions }                                                                                                             from './ProjectActivityUpdateAreaDetails';
import { HiresOverrideObject, ProjectActivityUpdateAreaResources, ProjectActivityUpdateAreaResourcesDisplayOptions }                                                                                    from './ProjectActivityUpdateAreaResources';
import { ProjectActivityUpdateAreaMeta }                                                                                                                                                                from './ProjectActivityUpdateAreaMeta';
import { PROJECT_ACTIVITY_UPDATE_TIMING_MODE, ProjectActivityUpdateAreaTiming, ProjectActivityUpdateAreaTimingDisplayOptions }                                                                          from './ProjectActivityUpdateAreaTiming';
import { WarningMessage }                                                                                                                                                                               from '../../../components/util/messaging/WarningMessage';
import { ProjectService, TimeService }                                                                                                                                                                  from '../../../../services';
import { ErrorBoundary }                                                                                                                                                                                from '../../../components/util/errorHandling/ErrorBoundary';
import { BasicDialog }                                                                                                                                                                                  from '../../../components/modals/BasicDialog';
import moment                                                                                                                                                                                           from 'moment';
import { ErrorService }                                                                                                                                                                                 from '../../../../services/ErrorService';
import { ProjectTimelineDateFieldObject }                                                                                                                                                               from "../../../../services/ProjectService";
import { ProjectQueries }                                                                                                                                                                               from "../../../../queries/ProjectQueries";


type EditorAreas = 'details' | 'budget' | 'timing' | 'resources' | 'meta';

export type ProjectActivityUpdateChangeSet = Partial<ProjectActivityUpdateDialogFormValues>;

export interface ProjectActivityUpdateDialogAreaDisplayOptions
    extends ProjectActivityUpdateAreaCostDisplayOptions,
        ProjectActivityUpdateAreaTimingDisplayOptions,
        ProjectActivityUpdateAreaDetailsDisplayOptions,
        ProjectActivityUpdateAreaResourcesDisplayOptions {
}

export interface ProjectActivityUpdateDialogProps {
    id: Uuid,
    projectId: Uuid,
    editorAreas?: ( EditorAreas )[],
    editorAreaOptions?: null | ProjectActivityUpdateDialogAreaDisplayOptions,
    hideUpdateAction?: ( featureFlags: UseFeatureFlagsHook ) => boolean,
    warningContent?: ReactNode,
    allowPropagationAction?: boolean,
    requireChangeSet?: boolean,
    requiredChangeSetAddProperties?: string,
    requiredChangeSetUpdateProperties?: string,
    closeOnEntityUpdate?: boolean,

    onUpdateComplete?: ProjectActivityEntityChangeCallback,

    onUpdateError?( err: any ): void

    onUpdateCanceled?( e: SystemEvent ): void,
}

export type ProjectActivityUpdateDialogFormValues = {
    operationalCostOverride: string,
    operationalCostOverrideNote: string,
    ongoingCostOverride: string,
    ongoingCostOverrideNote: string,

    recurrenceTypeOverride: CorvalRecurrenceType,
    recurrenceValueOverride: number,
    recurrenceUnitOverride: string,
    recurrenceValueUnitOverride: string,
    recurrenceMilestonesOverride: Array<ProjectMilestoneType | Uuid>,

    perceivedAsContinuousOverride: boolean,

    endTimeOverrideUtc: string,
    startTimeOverrideUtc: string,

    defaultTimeSpanValueOverride: number,
    defaultTimeSpanUnitOverride: string,
    projectTimingMilestoneOverride: ProjectMilestoneType | Uuid,
    projectTimingMilestoneOffsetMonthsOverride: number,

    hiresOverride: Array<HiresOverrideObject> | null,
    hireTypesApply: boolean,
}

type AreaComponentHashItem = { label: string, component: any, hasErrors: boolean };

type ProjectActivityUpdateDialogFormConfig = FormikConfig<ProjectActivityUpdateDialogFormValues>

const defaultProps: ProjectActivityUpdateDialogProps = {
    id:                                null,
    projectId:                         null,
    onUpdateComplete:                  null,
    onUpdateError:                     null,
    editorAreas:                       [ 'details' ],
    warningContent:                    null,
    allowPropagationAction:            false,
    requireChangeSet:                  false,
    requiredChangeSetAddProperties:    `id startDateUtc endDateUtc`,
    requiredChangeSetUpdateProperties: `id startDateUtc endDateUtc`,
    closeOnEntityUpdate:               false,
    hideUpdateAction:                  () => false,
}

const defaultProjectActivityAttributes: Record<keyof ProjectActivityUpdateDialogFormValues, any> = {
    operationalCostOverride:     '',
    operationalCostOverrideNote: '',
    ongoingCostOverride:         '',
    ongoingCostOverrideNote:     '',

    recurrenceTypeOverride:       CorvalRecurrenceType.Periodic,
    recurrenceValueOverride:      null,
    recurrenceUnitOverride:       null,
    recurrenceValueUnitOverride:  null,
    recurrenceMilestonesOverride: [],

    perceivedAsContinuousOverride: false,

    endTimeOverrideUtc:   '',
    startTimeOverrideUtc: '',

    defaultTimeSpanValueOverride:               null,
    defaultTimeSpanUnitOverride:                null,
    projectTimingMilestoneOverride:             null,
    projectTimingMilestoneOffsetMonthsOverride: '',

    hiresOverride:  [],
    hireTypesApply: true,
}

export interface ProjectActivityUpdateDialogContext {
    projectId: CorvalProjectActivity,
    projectActivity: CorvalProjectActivity,
    isLoading: boolean,
    timelineFields: ProjectTimelineDateFieldObject[],

    projectTimingOverrideType: PROJECT_ACTIVITY_UPDATE_TIMING_MODE,
    setProjectTimingOverrideType: UseStateHookFunction<PROJECT_ACTIVITY_UPDATE_TIMING_MODE>,
}

export const ProjectActivityUpdateDialogContext = React.createContext<ProjectActivityUpdateDialogContext>( null );

const ProjectActivityUpdateDialog: FC<ProjectActivityUpdateDialogProps> = ( props ) => {

    let { projectId }                                                 = props,
        [ isLoading, setIsLoading ]                                   = useState( false ),
        [ isProcessing, setIsProcessing ]                             = useState( false ),
        [ isReadyToClose, setIsReadyToClose ]                         = useState( false ),
        [ projectActivity, setProjectActivity ]                       = useState<CorvalProjectActivity>( null ),
        [ propagationActionModalOpen, setPropagationActionModalOpen ] = useState( false ),
        [ propagateChanges, setPropagateChanges ]                     = useState( false ),
        [ timelineFields, setTimelineFields ]                         = useState<ProjectTimelineDateFieldObject[]>( [] ),

        // Timing Mode State
        [ initialTimingOverrideType, setInitialTimingOverrideType ]   = useState<PROJECT_ACTIVITY_UPDATE_TIMING_MODE | null>( PROJECT_ACTIVITY_UPDATE_TIMING_MODE.MILESTONE_DURATION ),
        [ projectTimingOverrideType, setProjectTimingOverrideType ]   = useState<PROJECT_ACTIVITY_UPDATE_TIMING_MODE | null>( null ),

        { getUpdateProjectActivityInputData }                         = useProjectActivities(),
        { getAuditRecordTypes, getAuditRecordsFromEntity }            = useAuditRecords(),
        { includesErrorsIn }                                          = useCustomFormikHelpers(),
        { addStringIsDateFormatTest }                                 = useYupExtensions(),
        toastActions                                                  = useSystemToasts(),
        modalActions                                                  = useSystemModals(),
        formikRef                                                     = useRef<FormikProps<ProjectActivityUpdateDialogFormValues>>( {} as any ),
        confirmModalCallbackRef                                       = useRef<UseStateHookFunction<boolean>>( null ),
        featureFlags                                                  = useFeatureFlags();

    let editorAreaOptions = props.editorAreaOptions ?? {};

    /* Get Current Project Activity Request */
    const getProjectActivityReqFragment = `
         ${ DecisionEngineQueries.fragments.PROJECT_ACTIVITY.USER_EDIT_FIELDS }
		 ${ SystemQueries.fragments.AUDIT_RECORDS.QUERY_RETURN }
		 earliestAvailableStartDateUtc
		 project { 
            id 
            ${ ProjectQueries.fragments.PROJECTS.TIMELINE_BOUNDARY_FIELDS() }
            ${ ProjectQueries.fragments.PROJECTS.TIMELINE_FIELDS_AND_DEFINED_TIMINGS() }
         }
     `;

    const getProjectActivityReq = useQuery( DecisionEngineQueries.queries.GET_PROJECT_ACTIVITIES( getProjectActivityReqFragment ), {
        variables:   {
            projectId,
            includeGeneratedRecurrences: true,
            where:                       { id: { eq: props.id } }
        },
        fetchPolicy: 'network-only'
    } );

    /* Propagation Mutation Request */
    let [ propagateActivityChanges, propagateActivityChangesReq ] = useMutation( DecisionEngineQueries.mutations.PROPAGATE_PROJECT_ACTIVITY_CHANGES(
        `${ DecisionEngineQueries.fragments.PROJECT_ACTIVITY.USER_EDIT_FIELDS }
		 ${ SystemQueries.fragments.AUDIT_RECORDS.QUERY_RETURN }`
    ) );

    let dataIsLoading = queriesLoading( getProjectActivityReq ) || _.isNull( projectActivity );

    useEffect( () => {

        if( isReadyToClose && props.closeOnEntityUpdate ){

            modalActions.toggle( false );

        } else {

            setIsReadyToClose( false );
        }

    }, [ projectActivity ] )

    useEffect( () => {

        console.log( getProjectActivityReq.data );

        if( !queriesLoading( getProjectActivityReq ) && queriesHaveData( getProjectActivityReq ) && !isProcessing ){

            let projectActivity = _.first( getProjectActivityReq.data.projectActivites ) ?? null;

            let [ projectRecurrenceMilestones, customRecurrenceMilestones ] = ProjectService.getProjectActivityRecurrenceMilestones( projectActivity );

            setProjectActivity( projectActivity );

            // Set timeline Fields for milestone references
            setTimelineFields( ProjectService.getTimelineDataObjectFromProjectFields( projectActivity.project ) );

            // Capture initial timing mode
            if( _.isNull( projectTimingOverrideType ) ){

                const timingOverrideType = !_.isEmpty( projectActivity.projectTimingMilestoneOverride )
                                           ? PROJECT_ACTIVITY_UPDATE_TIMING_MODE.MILESTONE_DURATION
                                           : TimeService.hasTimeframeRange( [ projectActivity?.startTimeOverrideUtc, projectActivity?.endTimeOverrideUtc ] )
                                             ? PROJECT_ACTIVITY_UPDATE_TIMING_MODE.START_END_DATE
                                             : initialTimingOverrideType;

                setProjectTimingOverrideType( timingOverrideType );
            }

            // Set formik values
            formikRef.current.resetForm( {
                values: {
                    operationalCostOverride:     convertNullToEmptyString( projectActivity?.operationalCostOverride ?? null ),
                    operationalCostOverrideNote: convertNullToEmptyString( projectActivity?.operationalCostOverrideNote ?? null ),
                    ongoingCostOverride:         convertNullToEmptyString( projectActivity?.ongoingCostOverride ?? null ),
                    ongoingCostOverrideNote:     convertNullToEmptyString( projectActivity?.ongoingCostOverrideNote ?? null ),

                    recurrenceTypeOverride:      projectActivity?.recurrenceTypeOverride ?? CorvalRecurrenceType.Periodic,
                    recurrenceValueOverride:     projectActivity?.recurrenceValueOverride ?? null,
                    recurrenceUnitOverride:      projectActivity?.recurrenceUnitOverride ?? null,
                    recurrenceValueUnitOverride: ( projectActivity?.recurrenceValueOverride && projectActivity?.recurrenceUnitOverride )
                                                 ? combineValuesByDelimiter( [ projectActivity.recurrenceValueOverride, projectActivity.recurrenceUnitOverride ] ) : null,

                    // combine all recurrence milestone definition ids for the dropdown
                    recurrenceMilestonesOverride: [ ...projectRecurrenceMilestones, ...customRecurrenceMilestones ],

                    perceivedAsContinuousOverride: projectActivity?.perceivedAsContinuousOverride ?? false,

                    // specific start / end time overrides
                    startTimeOverrideUtc: projectActivity?.startTimeOverrideUtc ? TimeService.ISOtoDisplayDate( projectActivity?.startTimeOverrideUtc ) : null ?? null,
                    endTimeOverrideUtc:   projectActivity?.endTimeOverrideUtc ? TimeService.ISOtoDisplayDate( projectActivity?.endTimeOverrideUtc ) : null ?? null,

                    // specific start time via milestone + duration overrides
                    defaultTimeSpanValueOverride:               projectActivity.defaultTimeSpanValueOverride ?? null,
                    defaultTimeSpanUnitOverride:                projectActivity.defaultTimeSpanUnitOverride ?? null,
                    projectTimingMilestoneOverride:             projectActivity.projectTimingMilestoneOverride
                                                                ? ProjectService.getMilestoneInfoFromType( projectActivity.projectTimingMilestoneOverride )?.PROJECT_FIELD ?? null
                                                                : projectActivity.projectCustomTimingMilestoneId ?? null,
                    projectTimingMilestoneOffsetMonthsOverride: projectActivity.projectTimingMilestoneOffsetMonthsOverride ?? null,

                    // prepare hire override fields
                    hiresOverride:  projectActivity.hiresOverride ? projectActivity.hiresOverride.map( override => withoutType( override ) ) : [],
                    hireTypesApply: !( _.isArray( projectActivity.hiresOverride ) && projectActivity.hiresOverride.length === 0 )
                }
            } );
        }

    }, [ getProjectActivityReq, initialTimingOverrideType ] );

    let getUpdatedDataObject = () => {

        let inputData      = {},
            omitProperties = [ 'hireTypesApply' ];

        /* Collect only the form values that were changed, and that apply to the model */
        _.mapObject( _.omit( formikRef.current.values, omitProperties ), ( value, key ) => {

            if( key == 'hiresOverride' ){

                inputData[ key ] = getDispatchValueOfHiresOverride();

            } else if( formikRef.current.initialValues[ key ] !== value ){

                inputData[ key ] = convertEmptyStringToNull( value );
            }

        } );

        return inputData;
    }

    const getDispatchValueOfHiresOverride = () => {

        let hiresOverrideValue  = formikRef.current.values.hiresOverride,
            hireTypesApplyValue = formikRef.current.values.hireTypesApply,
            hiresOverride       = hireTypesApplyValue ? ( hiresOverrideValue.length > 0 ) ? hiresOverrideValue : null : [];

        return hiresOverride;
    }

    const isUsingMilestoneTiming = () => {

        const milestoneOverride = formikRef.current.values.projectTimingMilestoneOverride;

        return !_.isEmpty( milestoneOverride );
    }

    const isUsingCustomMilestoneTiming = () => {

        const milestoneOverride = formikRef.current.values.projectTimingMilestoneOverride;

        if( !isUsingMilestoneTiming() ) return false;

        return ProjectService.timelineDataObjectIsCustomMilestone( ProjectService.getTimelineDataObjectFromSet( milestoneOverride, timelineFields ) );
    }

    let getGraphArgs = () => {

        if( !projectActivity || !( formikRef.current?.values ) ) return {};

        let canUpdateOpCost          = featureFlags.enableProjectActivityOperationalCostEditing,
            canUpdateOngoingCost     = featureFlags.enableProjectActivityOngoingCostEditing,
            canUpdateRecurrence      = featureFlags.enableProjectActivityRecurrenceEditing,
            canUpdateStartTime       = featureFlags.permissionProjectActivityUpdateStartDate,
            canUpdateEndTime         = featureFlags.permissionProjectActivityUpdateEndDate,
            canUpdateDuration        = featureFlags.permissionProjectActivityUpdateDuration,
            canUpdateTimingMilestone = featureFlags.permissionProjectActivityUpdateTimingMilestone,
            canUpdateHireTypes       = featureFlags.permissionProjectActivityUpdateHireTypes;

        let proposedRecurrenceType    = formikRef.current.values.recurrenceTypeOverride,
            frequencyRecurrencesEmpty = _.isEmpty( formikRef.current.values.recurrenceValueUnitOverride ),
            milestoneRecurrencesEmpty = _.isEmpty( formikRef.current.values.recurrenceMilestonesOverride ),
            finalRecurrenceType       = (
                ( formikRef.current.values.recurrenceTypeOverride === CorvalRecurrenceType.Periodic )
                ? frequencyRecurrencesEmpty ? null : proposedRecurrenceType
                : milestoneRecurrencesEmpty ? null : proposedRecurrenceType
            )

        // convert override timings
        let startTimeOverride = convertEmptyStringToNull( formikRef.current.values.startTimeOverrideUtc ),
            endTimeOverride   = convertEmptyStringToNull( formikRef.current.values.endTimeOverrideUtc );

        if( startTimeOverride ){
            startTimeOverride = TimeService.displayDateToISO( startTimeOverride )
        }
        if( endTimeOverride ){
            endTimeOverride = TimeService.displayDateToISO( endTimeOverride )
        }

        // sort milestone overrides between project field names and custom milestone ids
        let [ systemMilestoneOverrides, customMilestoneOverrides ] = ProjectService.sortProjectActivityRecurrenceMilestoneSet( formikRef.current.values.recurrenceMilestonesOverride );

        // get hiretype overrides
        let hiresOverride = getDispatchValueOfHiresOverride();

        // get required model data for mutation to occur
        let updateData = getUpdateProjectActivityInputData( projectActivity );

        return ( {
            variables: {
                input: {
                    projectId:         projectId,
                    projectActivities: [
                        {
                            // required to include in mutation
                            ...updateData,

                            // operational cost
                            operationalCostOverride:     canUpdateOpCost ? convertEmptyStringToNull( formikRef.current.values.operationalCostOverride ) : projectActivity.operationalCostOverride,
                            operationalCostOverrideNote: canUpdateOpCost ? convertEmptyStringToNull( formikRef.current.values.operationalCostOverrideNote ) : projectActivity.operationalCostOverrideNote,

                            // ongoing cost
                            ongoingCostOverride:     canUpdateOngoingCost ? convertEmptyStringToNull( formikRef.current.values.ongoingCostOverride ) : projectActivity.ongoingCostOverride,
                            ongoingCostOverrideNote: canUpdateOngoingCost ? convertEmptyStringToNull( formikRef.current.values.ongoingCostOverrideNote ) : projectActivity.ongoingCostOverrideNote,

                            // recurrence scheduling
                            recurrenceTypeOverride:       canUpdateRecurrence ? finalRecurrenceType : projectActivity.recurrenceTypeOverride,
                            recurrenceValueOverride:      canUpdateRecurrence ? formikRef.current.values.recurrenceValueOverride : projectActivity.recurrenceValueOverride,
                            recurrenceUnitOverride:       canUpdateRecurrence ? formikRef.current.values.recurrenceUnitOverride : projectActivity.recurrenceUnitOverride,
                            recurrenceMilestonesOverride: canUpdateRecurrence ? systemMilestoneOverrides : projectActivity.recurrenceMilestonesOverride,
                            recurrenceCustomMilestoneIds: canUpdateRecurrence ? customMilestoneOverrides : projectActivity.recurrenceCustomMilestones,

                            // timing display
                            perceivedAsContinuousOverride: formikRef.current.values.perceivedAsContinuousOverride ?? !!( projectActivity.perceivedAsContinuousOverride ),

                            // timing overrides
                            startTimeOverrideUtc: canUpdateEndTime ? ( isUsingMilestoneTiming() ? null : startTimeOverride ) : projectActivity.startTimeOverrideUtc,
                            endTimeOverrideUtc:   canUpdateStartTime ? ( isUsingMilestoneTiming() ? null : endTimeOverride ) : projectActivity.endTimeOverrideUtc,

                            // milestone / duration overrides
                            defaultTimeSpanValueOverride:               canUpdateDuration ? formikRef.current.values.defaultTimeSpanValueOverride : projectActivity.defaultTimeSpanValueOverride,
                            defaultTimeSpanUnitOverride:                canUpdateDuration ? formikRef.current.values.defaultTimeSpanUnitOverride : projectActivity.defaultTimeSpanUnitOverride,
                            projectTimingMilestoneOverride:             canUpdateTimingMilestone ? ( isUsingMilestoneTiming() ? ( isUsingCustomMilestoneTiming() ? null : ProjectService.getMilestoneTypeFromField( formikRef.current.values.projectTimingMilestoneOverride ) ) : null ) : projectActivity.projectTimingMilestoneOverride,
                            projectCustomTimingMilestoneId:             canUpdateTimingMilestone ? ( isUsingMilestoneTiming() ? ( isUsingCustomMilestoneTiming() ? formikRef.current.values.projectTimingMilestoneOverride : null ) : null ) : projectActivity.projectCustomTimingMilestoneId,
                            projectTimingMilestoneOffsetMonthsOverride: canUpdateTimingMilestone ? convertEmptyStringToNull( formikRef.current.values.projectTimingMilestoneOffsetMonthsOverride ) : projectActivity.projectTimingMilestoneOffsetMonthsOverride,

                            // hire overrides
                            hiresOverride: canUpdateHireTypes ? hiresOverride : projectActivity.hiresOverride,
                        }
                    ]
                }
            }
        } );
    }

    let handlePropagationAction: QueryDialogProps['confirmationHandler'] = ( setConfirmed ) => {

        if( props.allowPropagationAction ){

            confirmModalCallbackRef.current = setConfirmed;

            setPropagationActionModalOpen( true );

        } else {

            setConfirmed( true );
        }

    }

    const handlePropagateOperationConfirmed = () => {

        if( _.isFunction( confirmModalCallbackRef.current ) ){

            setPropagateChanges( true );

            setPropagationActionModalOpen( false );

            confirmModalCallbackRef.current( true );
            // confirmModalCallbackRef.current( false );
        }
    }

    const handlePropagateOperationSkipped = () => {

        if( _.isFunction( confirmModalCallbackRef.current ) ){

            setPropagateChanges( false );

            setPropagationActionModalOpen( false );

            confirmModalCallbackRef.current( true );
            // confirmModalCallbackRef.current( false );
        }
    }

    const handleOnRequestSubmit: QueryDialogProps['onSubmit'] = () => {

        setIsLoading( true );
        setIsProcessing( true );
        setIsReadyToClose( false );
    }

    const handleOnRequestComplete: QueryDialogProps['onComplete'] = async ( res ) => {

        let propagationEntities = null;

        if( res ){

            if( propagateChanges ){

                toastActions.success( 'Activity Updated!' );

                const changeVars = {
                    variables: {
                        input: {
                            projectId,
                            originatingActivityId: projectActivity.activity.id,
                            propagationData:       {
                                ...getUpdatedDataObject()
                            },
                        }
                    }
                }

                // Call propagate endpoint with the details
                try{

                    const propagationResult = await propagateActivityChanges( changeVars );

                    if( !propagationResult.errors ){

                        toastActions.success( 'All Activity Instances Updated!' );

                        propagationEntities = propagationResult.data.propagateProjectActivityValues;

                    } else {

                        toastActions.error( 'Activity Propagation Failed!' );
                    }

                } catch( e ) {

                    toastActions.error( e.message );
                    // Something bad happened.
                }
            }

            // Get the change set if required
            const entityChangeSet = ( props.requireChangeSet )
                                    ? res?.updateProjectActivitiesWithChangeset ?? null
                                    : null;

            setIsLoading( false );

            // Reset the form itself.
            formikRef.current.resetForm( { values: formikRef.current.values } );

            // Reset propagation choice
            setPropagateChanges( false );

            setIsReadyToClose( true );

            // Refetch the set, updating cache models
            getProjectActivityReq.refetch().then( res => {

                let projectActivity = _.first( res.data.projectActivites ) ?? null;

                setProjectActivity( projectActivity );

                // Run post update method w/ delta objects
                runIfFunction(
                    props.onUpdateComplete,
                    getUpdatedDataObject(),
                    projectActivity.id,
                    propagateActivityChangesReq.called,
                    propagationEntities,
                    entityChangeSet
                );

                setIsProcessing( false );

            } );

        } else {

            setIsProcessing( false );
        }
    }

    const handleOnRequestError: QueryDialogProps['onError'] = async ( error: Error ) => {

        setIsLoading( false );
        setIsReadyToClose( false );

        toastActions.error( 'Activity Not Updated!', ErrorService.getGQLErrorMessage( error ) ?? 'Something prevented Corval from updating the activity. Please try again.' );

        runIfFunction( props.onUpdateError, formikRef.current.values );
    }

    const handleOnRequestCancelled: QueryDialogProps['onClose'] = ( e ) => {

        setIsLoading( false );
        setIsReadyToClose( false );

        runIfFunction( props.onUpdateCanceled, e );
    }

    const handleFormReset = () => {

        formikRef.current.resetForm();

        setProjectTimingOverrideType( initialTimingOverrideType );
    }

    const getMappedRequestData = (): ProjectActivityUpdateDialogFormValues => {

        return defaultProjectActivityAttributes;
    }

    const ensureProjectActivityStartDateStartsFirst = ( _startDate ) => {

        let _endDate = formikRef.current.values?.endTimeOverrideUtc ?? null;

        if( _.isEmpty( _startDate ) ) return true;
        if( _.isEmpty( _endDate ) ) return true;

        let endDate   = TimeService.displayDateToMoment( _endDate ),
            startDate = TimeService.displayDateToMoment( _startDate );

        return startDate.isBefore( endDate );
    }

    // add display date validation
    addStringIsDateFormatTest()

    let formProps: ProjectActivityUpdateDialogFormConfig = {
        innerRef:           formikRef,
        isInitialValid:     false,
        enableReinitialize: false,
        initialValues:      getMappedRequestData(),
        validationSchema:   Yup.object().shape( {
            _startTimeOverrideUtc:       Yup.ref( 'startTimeOverrideUtc' ),
            _endTimeOverrideUtc:         Yup.ref( 'endTimeOverrideUtc' ),
            operationalCostOverride:     Yup.number().nullable()
                                            .min( 0, 'Cost must have a non-negative number.' )
                                            .when( 'operationalCostOverrideNote', {
                                                is:   value => ( value && value.length > 0 ),
                                                then: Yup.number().required( 'Value is required if leaving a note.' )
                                            } ),
            ongoingCostOverride:         Yup.number().nullable()
                                            .min( 0, 'Cost must have a non-negative number.' )
                                            .when( 'ongoingCostOverrideNote', {
                                                is:   value => ( value && value.length > 0 ),
                                                then: Yup.number().required( 'Value is required if leaving a note.' )
                                            } ),
            operationalCostOverrideNote: Yup.string().nullable().min( 1, 'Please enter a clear and explanative description of this cost.' ),
            ongoingCostOverrideNote:     Yup.string().nullable().min( 1, 'Please enter a clear and explanative description of this cost.' ),
            startTimeOverrideUtc:        Yup.string()
                                            .nullable()
                                            .test(
                                                'Check earliest available start date',
                                                'Start date must come after its parent dependency.',
                                                async ( value ) => {

                                                    if( !value ) return true;

                                                    const earliestStartDate  = projectActivity.earliestAvailableStartDateUtc,
                                                          currentValueMoment = TimeService.displayDateToMoment( value ),
                                                          validBoundaryDate  = ( !_.isEmpty( earliestStartDate ) && moment( earliestStartDate ).isValid() );

                                                    return ( validBoundaryDate )
                                                           ? currentValueMoment.isSameOrAfter( TimeService.ISOtoMoment( earliestStartDate ) )
                                                           : true;
                                                }
                                            )
                                            .when( [ 'endTimeOverrideUtc' ], {
                                                is:   value => !_.isEmpty( value ),
                                                then: Yup.string()
                                                         .isDateFormat()
                                                         .test( {
                                                             name:    'Ensure project start date is before end date',
                                                             message: `Project start date must come before end date.`,
                                                             test:    ensureProjectActivityStartDateStartsFirst
                                                         } )
                                            } ),
            endTimeOverrideUtc:          Yup.string()
                                            .nullable()
                                            .when( [ 'startTimeOverrideUtc' ], {
                                                is:   value => !_.isEmpty( value ),
                                                then: Yup.string()
                                                         .isDateFormat()
                                                         .required( 'Start and end time must be specified' ),
                                            } ),

            projectTimingMilestoneOverride:             Yup.string().nullable(),
            projectTimingMilestoneOffsetMonthsOverride: Yup.number().nullable().when( [ 'projectTimingMilestoneOverride' ], {
                is:   ( value ) => !_.isEmpty( value ),
                then: Yup.number().required( 'An offset value is required' )
            } ),
            defaultTimeSpanValueOverride:               Yup.number().nullable().when( [ 'projectTimingMilestoneOverride' ], {
                is:   ( value ) => !_.isEmpty( value ),
                then: Yup.number().required( 'A duration value is required' ).min( 1, 'Duration must be at least 1.' )
            } ),
            defaultTimeSpanUnitOverride:                Yup.string().nullable().when( [ 'projectTimingMilestoneOverride' ], {
                is:   ( value ) => !_.isEmpty( value ),
                then: Yup.string().required( 'A duration unit is required' )
            } ),

            hiresOverride: Yup.array().of( Yup.object( {
                hireTypeId: Yup.string().defined().required( 'Please select a hire type to include.' ),
                headcount:  Yup.number().min( 1, 'At least one of this hire type must be specified.' ).required( 'Please specify a hire type quantity required.' ),
            } ) ),

        }, [
            [ 'startTimeOverrideUtc', 'endTimeOverrideUtc' ],
        ] ),
        onSubmit:           null,
    };

    const produceTabForEditorArea = ( editorAreaDescriptorObject: AreaComponentHashItem ) => {

        let { label, hasErrors, component: Component } = editorAreaDescriptorObject

        return (
            {
                menuItem: <Menu.Item key={ label }><FormTabWithNotifier label={ label } active={ hasErrors }/></Menu.Item>,
                render:   () => (
                    <Tab.Pane className={ 'no-border' }>

                        <Component { ...editorAreaOptions }/>

                    </Tab.Pane>
                )
            }
        )
    }

    return (

        <Formik { ...formProps }>

            { formik => {

                let hasTabs           = ( props.editorAreas.length > 1 ),
                    hasDetailErrors   = includesErrorsIn( formik, 'displayName', 'description' ),
                    hasCostErrors     = includesErrorsIn( formik, 'costAdjustmentNote', 'initialCost', 'ongoingCost' ),
                    hasResourceErrors = false,
                    hasTimingErrors   = false;

                let singleNamedArea = hasTabs ? null : _.first( props.editorAreas ),
                    tabbedAreas     = props.editorAreas;

                let areaComponentHash: Record<EditorAreas, AreaComponentHashItem> = {
                    details:   { label: 'Details', component: ProjectActivityUpdateAreaDetails, hasErrors: hasDetailErrors },
                    budget:    { label: 'Costs', component: ProjectActivityUpdateAreaCost, hasErrors: hasCostErrors },
                    resources: { label: 'Resourcing', component: ProjectActivityUpdateAreaResources, hasErrors: hasResourceErrors },
                    timing:    { label: 'Timing', component: ProjectActivityUpdateAreaTiming, hasErrors: hasTimingErrors },
                    meta:      { label: 'Settings', component: ProjectActivityUpdateAreaMeta, hasErrors: false },
                };

                let EditorAreaComponent = hasTabs ? null : areaComponentHash[ singleNamedArea ].component;

                // Display margins
                let marginSize = hasTabs ? 40 : 55;

                return (

                    <>
                        {/* Propagation Dialog (second modal) */ }
                        <Modal open={ propagationActionModalOpen } size='tiny'>

                            <BasicDialog
                                operationIsDangerous={ true }
                                hasSystemContext={ false }
                                onComplete={ handlePropagateOperationConfirmed }
                                onClose={ handlePropagateOperationSkipped }
                                submitLabel={ 'Apply to all' }
                                cancelLabel={ 'Just this activity' }
                                className={ 'no-padding' }>

                                <BasicDialogContainer title={ 'Propagate Changes?' } titleColor={ SemanticUIColor.ORANGE }>

                                    <>Do you want to apply the changes made here to all instances of this activity?</>

                                </BasicDialogContainer>

                            </BasicDialog>

                        </Modal>

                        <QueryOpDialog
                            showOperationInTransitMessage={ false }
                            showOperationInTransitOnSubmit={ true }
                            operationIsHidden={ props.hideUpdateAction( featureFlags ) }
                            operationDisabled={ isLoading || !formik.isValid || !formik.dirty }
                            closeIsDisabled={ isProcessing || isLoading }
                            mutation={
                                props.requireChangeSet
                                ? DecisionEngineQueries.mutations.UPDATE_PROJECT_ACTIVITIES_WITH_CHANGESET( `
                                    added { ${ props.requiredChangeSetAddProperties ?? getProjectActivityReqFragment } }
                                    updated { ${ props.requiredChangeSetUpdateProperties ?? getProjectActivityReqFragment } }
                                    removed
                                ` )
                                : DecisionEngineQueries.mutations.UPDATE_PROJECT_ACTIVITIES( getProjectActivityReqFragment ) }
                            graphArgs={ getGraphArgs }
                            confirmationHandler={ props.allowPropagationAction ? handlePropagationAction : null }
                            onSubmit={ handleOnRequestSubmit }
                            onComplete={ handleOnRequestComplete }
                            onError={ handleOnRequestError }
                            onClose={ handleOnRequestCancelled }
                            cancelLabel={ 'Close' }
                            submitLabel={ 'Update Activity' }
                            successToastLabel={ null }
                            overflow={ true }
                            className={ 'no-padding' }
                            alignControls={ Align.CENTER }>

                            <BasicDialogContainer title={ 'Activity Editor' } contentPadding={ null } overflow={ true }>

                                <ErrorBoundary>

                                    { dataIsLoading
                                      ? (
                                          /* The modal dimmer makes this perma-white text, so construct a different loader than normal*/
                                          <CenteredContentContainer className={ 'padding-lg' }>
                                              <><Icon loading name='circle notch'/> Retrieving Activity Data...</>
                                          </CenteredContentContainer>

                                      ) : ( <Form>

                                                {/* PA DETAILS */ }
                                                <DialogIntroductionContainer
                                                    title={ projectActivity.activity.displayName }
                                                    description={ projectActivity.activity.description }
                                                    descriptionContent={ (
                                                        <>
                                                            {/* AUDIT ENTRY MESSAGE */ }
                                                            { hasTabs && (
                                                                <AuditMemo record={ _.first( getAuditRecordTypes( getAuditRecordsFromEntity( projectActivity ), null, 'newest' ) ) }
                                                                           actionDesc={ 'last' }
                                                                           actionVerb={ 'updated' }
                                                                           auditSubject={ 'this activity' }/>
                                                            ) }
                                                        </>
                                                    ) }
                                                    iconSpace={ 35 }
                                                    iconCentered={ true }
                                                    icon={ ENTITY_ICONS.ACTIVITIES.ENTITY }
                                                    utilityContent={ (
                                                        <List horizontal style={ { paddingRight: 30 } }>

                                                            <List.Item className={ classNames( { 'opacity--0': !formik.dirty }, 'no-padding' ) }>

                                                                <Button basic
                                                                        type={ 'button' }
                                                                        size={ 'mini' }
                                                                        icon={ 'undo' }
                                                                        content={ 'Undo Changes' }
                                                                        onClick={ handleFormReset }
                                                                        disabled={ isLoading || !formik.dirty }/>

                                                            </List.Item>

                                                        </List>
                                                    ) }
                                                />

                                                {/* INFORMATION */ }
                                                { props.warningContent && (
                                                    <StyledContainer margin={ [ Size.XS, Size.LG, null, Size.LG ] } textAlign={ Align.CENTER }>
                                                        <WarningMessage size={ 'mini' } icon={ null }>{ props.warningContent }</WarningMessage>
                                                    </StyledContainer>
                                                ) }

                                                {/* CONTENT */ }
                                                <StyledContainer padding={ [ Size.MD, null, Size.LG, null ] }>

                                                    <Grid columns={ `${ marginSize }px auto ${ marginSize }px` } rows={ `minmax(${ hasTabs ? '300px' : 'auto' },auto)` } columnGap={ '0px' }>

                                                        <Cell top={ 1 } left={ 2 } width={ 1 } className={ 'position--relative' }>

                                                            <ProjectActivityUpdateDialogContext.Provider value={ {
                                                                projectId,
                                                                projectActivity,
                                                                timelineFields,
                                                                isLoading,
                                                                projectTimingOverrideType,
                                                                setProjectTimingOverrideType,
                                                            } }>

                                                                {/* Form */ }
                                                                { hasTabs ? (

                                                                    <Tab
                                                                        defaultActiveIndex={ 0 }
                                                                        menu={ { secondary: true, pointing: true, } }
                                                                        panes={ _.values( _.pick( areaComponentHash, tabbedAreas ) ).map( produceTabForEditorArea ) }/>

                                                                ) : (

                                                                      <StyledContainer padding={ [ null, null, Size.MD, null ] }>

                                                                          <EditorAreaComponent { ...editorAreaOptions }/>

                                                                      </StyledContainer>
                                                                  ) }

                                                            </ProjectActivityUpdateDialogContext.Provider>

                                                        </Cell>
                                                    </Grid>

                                                </StyledContainer>

                                            </Form>
                                      ) }

                                </ErrorBoundary>

                            </BasicDialogContainer>

                        </QueryOpDialog>

                    </>
                )
            } }
        </Formik>
    );
};

ProjectActivityUpdateDialog.defaultProps = defaultProps;

export { ProjectActivityUpdateDialog }
